import {
  DecoratorNode,
  LexicalNode,
  NodeKey,
  SerializedLexicalNode,
  Spread,
} from "lexical";
import React, { ReactElement } from "react";

export interface SerializedSearchHighlightNode
  extends Spread<
    {
      text: string;
      isActive: boolean;
      matchId: string;
      originalFormat?: number;
      originalStyle?: string;
    },
    SerializedLexicalNode
  > {}

export class SearchHighlightNode extends DecoratorNode<ReactElement> {
  __text: string;
  __isActive: boolean;
  __matchId: string;
  __originalFormat?: number;
  __originalStyle?: string;

  static getType(): string {
    return "search-highlight";
  }

  static clone(node: SearchHighlightNode): SearchHighlightNode {
    return new SearchHighlightNode(
      node.__text,
      node.__isActive,
      node.__matchId,
      node.__key,
      node.__originalFormat,
      node.__originalStyle
    );
  }

  constructor(
    text: string,
    isActive: boolean = false,
    matchId: string,
    key?: NodeKey,
    originalFormat?: number,
    originalStyle?: string
  ) {
    super(key);

    this.__text = text;
    this.__isActive = isActive;
    this.__matchId = matchId;
    this.__originalFormat = originalFormat;
    this.__originalStyle = originalStyle;
  }

  createDOM(): HTMLElement {
    // For DecoratorNode, we should not implement createDOM when we have decorate()
    // The decorate() method handles the rendering
    const element = document.createElement("span");
    return element;
  }

  updateDOM(): boolean {
    // For DecoratorNode with decorate(), always return true to let React handle updates
    return true;
  }

  static importJSON(
    serializedNode: SerializedSearchHighlightNode
  ): SearchHighlightNode {
    const { text, isActive, matchId, originalFormat, originalStyle } =
      serializedNode;
    return $createSearchHighlightNode(
      text,
      isActive,
      matchId,
      originalFormat,
      originalStyle
    );
  }

  exportJSON(): SerializedSearchHighlightNode {
    return {
      text: this.__text,
      isActive: this.__isActive,
      matchId: this.__matchId,
      originalFormat: this.__originalFormat,
      originalStyle: this.__originalStyle,
      type: "search-highlight",
      version: 1,
    };
  }

  decorate(): ReactElement {
    return React.createElement(
      "span",
      {
        className: this.__isActive
          ? "search-highlight-active"
          : "search-highlight",
        "data-lexical-search-highlight": "true",
        "data-match-id": this.__matchId,
      },
      this.__text
    );
  }

  getText(): string {
    return this.__text;
  }

  getTextContent(): string {
    return this.__text;
  }

  getIsActive(): boolean {
    return this.__isActive;
  }

  getMatchId(): string {
    return this.__matchId;
  }

  getOriginalFormat(): number | undefined {
    return this.__originalFormat;
  }

  getOriginalStyle(): string | undefined {
    return this.__originalStyle;
  }

  setIsActive(isActive: boolean): void {
    const writable = this.getWritable();
    writable.__isActive = isActive;
  }

  isInline(): boolean {
    return true;
  }
}

export function $createSearchHighlightNode(
  text: string,
  isActive: boolean = false,
  matchId: string,
  originalFormat?: number,
  originalStyle?: string
): SearchHighlightNode {
  return new SearchHighlightNode(
    text,
    isActive,
    matchId,
    undefined,
    originalFormat,
    originalStyle
  );
}

export function $isSearchHighlightNode(
  node: LexicalNode | null | undefined
): node is SearchHighlightNode {
  return node instanceof SearchHighlightNode;
}
